import { useEffect, useMemo, useState } from "react";
import type { BarConfig } from "../../../components/Charts";
import chartDataFetcher, { type P5RawDataPoint } from "../../../services/api/ChartDataFetch";

export interface P5FilterValues {
  comarca?: string;
  sector?: string;
}

export interface P5ChartPoint extends Record<string, string | number> {
  nom_comarca: string;
}

const SECTOR_COLORS = [
  "#3498DB", "#E74C3C", "#2ECC71", "#9B59B6",
  "#1ABC9C", "#F39C12", "#E67E22", "#34495E",
  "#16A085", "#8E44AD",
];

function pivotBySector(filtered: P5RawDataPoint[]): {
  chartData: P5ChartPoint[];
  bars: BarConfig[];
} {
  const comarcas = Array.from(new Set(filtered.map((d) => d.nom_comarca))).sort();
  const sectors = Array.from(new Set(filtered.map((d) => d.nom_sector))).sort();

  const chartData: P5ChartPoint[] = comarcas.map((comarca) => {
    const point: P5ChartPoint = { nom_comarca: comarca };
    const rows = filtered.filter((d) => d.nom_comarca === comarca);
    sectors.forEach((sector) => {
      const row = rows.find((d) => d.nom_sector === sector);
      if (row) point[sector] = row.iaup;
    });
    return point;
  });

  const bars: BarConfig[] = sectors.map((sector, i) => ({
    key:   sector,
    label: sector,
    color: SECTOR_COLORS[i % SECTOR_COLORS.length],
  }));

  return { chartData, bars };
}

export function useP5Data(filters: P5FilterValues) {
  const [rawData, setRawData] = useState<P5RawDataPoint[]>(
    () => chartDataFetcher.getCachedSync<P5RawDataPoint[]>("p5") ?? []
  );
  const [loading, setLoading] = useState(
    () => chartDataFetcher.getCachedSync<P5RawDataPoint[]>("p5") === null
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (chartDataFetcher.getCachedSync("p5") !== null) return;

    let cancelled = false;

    setLoading(true);
    setError(null);

    chartDataFetcher
      .getP5Data()
      .then((data) => { if (!cancelled) setRawData(data); })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Error carregant dades");
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  const availableComarques = useMemo(
    () => Array.from(new Set(rawData.map((d) => d.nom_comarca))).sort(),
    [rawData]
  );

  const availableSectors = useMemo(
    () => Array.from(new Set(rawData.map((d) => d.nom_sector))).sort(),
    [rawData]
  );

  const filtered = useMemo(() => {
    let data = rawData;
    if (filters.comarca) data = data.filter((d) => d.nom_comarca === filters.comarca);
    if (filters.sector) data = data.filter((d) => d.nom_sector === filters.sector);
    return data;
  }, [rawData, filters.comarca, filters.sector]);

  const { chartData, bars } = useMemo(() => pivotBySector(filtered), [filtered]);

  return { chartData, bars, availableComarques, availableSectors, loading, error };
}
